import React, { lazy, Suspense } from 'react';
import { Route, Routes as RouterRoutes, Navigate } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import GuardedRoute from './components/_guards/GuardedRoute';
import { userTypeEnum } from './enums/auth/userType.enum';
import { userPermissionsEnum } from './enums/auth/userPermissions.enum';

// Lazy loaded imports organized by domain

// AUTH
const LoginScreen = lazy(() => import('./screens/Auth/LoginScreen'));
const SignUpScreen = lazy(() => import('./screens/Auth/SignUpScreen'));
const ForgotPasswordScreen = lazy(() => import('./screens/Auth/ForgotPasswordScreen'));
const ResetPasswordScreen = lazy(() => import('./screens/Auth/ResetPasswordScreen'));
//MAIN
//CHARGER
const ChargePointsScreen = lazy(() => import('./screens/Charger/ChargePointsScreen'));
const AddChargerScreen = lazy(() => import('./screens/Charger/AddChargerScreen'));
const EditChargerScreen = lazy(() => import('./screens/Charger/EditChargerScreen'));
const ConnectorListScreen = lazy(() => import('./screens/Connector/ConnectorListScreen'));
const ConnectorScreen = lazy(() => import('./screens/Connector/ConnectorScreen'));
//CONNECTOR
//GROUP
const MyGroups = lazy(() => import('./screens/Group/MyGroups'));
const AddOrEditGroupScreen = lazy(() => import('./screens/Group/AddOrEditGroupScreen'));

//BUSINESS GROUP
const MyBusinessGroups = lazy(() => import('./screens/BusinessGroup/MyBusinessGroups'));
const AddOrEditBusinessGroupScreen = lazy(() => import('./screens/BusinessGroup/AddOrEditBusinessGroupScreen'));
//DRIVER
const DriversScreen = lazy(() => import('./screens/Driver/DriversScreen'));
const AddDriverScreen = lazy(() => import('./screens/Driver/AddDriverScreen'));
const EditDriverScreen = lazy(() => import('./screens/Driver/EditDriverScreen'));
const PermissionsChecklist = lazy(() => import('./screens/Driver/PermissionsChecklist'));

// CAR
const MyCars = lazy(() => import('./screens/Car/MyCars'));
const AddCarScreen = lazy(() => import('./screens/Car/AddCarScreen'));

// PROFILE
const MyProfilesScreen = lazy(() => import('./screens/Profile/MyProfilesScreen'));
const AddProfileScreen = lazy(() => import('./screens/Profile/AddProfileScreen'));
const EditProfileScreen = lazy(() => import('./screens/Profile/EditProfileScreen'));

// USER
const PersonalScreen = lazy(() => import('./screens/User/PersonalScreen'));

// SETTINGS
const SettingsScreen = lazy(() => import('./screens/Settings/SettingsScreen'));

// TERMS & CONDITIONS
const AccessibilityStatementScreen = lazy(() => import('./screens/Terms-Conditions/AccessibilityStatementScreen'));
const PrivacyPolicyScreen = lazy(() => import('./screens/Terms-Conditions/PrivacyPolicyScreen'));

// BILLING, INVOICE & HISTORY
const BillsScreen = lazy(() => import('./screens/Billing-Invoice-History/BillsScreen'));
const PaymentsScreen = lazy(() => import('./screens/Billing-Invoice-History/PaymentsScreen'));
const UpdateBillingScreen = lazy(() => import('./screens/Billing-Invoice-History/UpdateBillingScreen'));

const ChargingStrategiesScreen = lazy(() => import('./screens/ChargingStrategy/ChargingStrategyScreen'));
const AddOrUpdateStrategyScreen = lazy(() => import('./screens/ChargingStrategy/AddOrUpdateStrategyScreen'));

//CARDS
const RFIDTagsScreen = lazy(() => import('./screens/Tags/RFIDTagsScreen'));
const AddRFIDTagScreen = lazy(() => import('./screens/Tags/AddRFIDTagScreen'));

// MISC
const QrScannarScreen = lazy(() => import('./screens/QrScannerScreen'));
const LocationsScreen = lazy(() => import('./screens/Locations/LocationsScreen'));
const AddLocationScreen = lazy(() => import('./screens/Locations/AddLocationScreen'));
const OperatorChargePoints = lazy(() => import('./screens/Charger/OperatorChargePoints'));
const QRCodeRouteHandler = lazy(() => import('./components/QRCodeRouteHandler'));
const OldFormatQRCodeRouteHandler = lazy(() => import('./components/OldFormatQRCodeRouteHandler'));

// Loading fallback component
const LoadingFallback = () => (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
    </Box>
);

/**
 * ROUTING ARCHITECTURE NOTES (React Router v6)
 *
 * GuardedRoute is now a children-wrapper (not a Route).
 * Pattern: <Route path='/x' element={<GuardedRoute {...DEFAULT_GUARD}><Screen /></GuardedRoute>} />
 *
 * In v6, all routes are exact by default. Remove `exact` prop everywhere.
 * Routes uses smart ranking so ordering matters less, but we keep specific paths first for clarity.
 * QR catch-all /:partyID/:uid/:connector_id is all-dynamic so ranks below specific static routes.
 */

// Default guard config - blocks OperationManager with View permission
const DEFAULT_GUARD = {
    blockedTypesRoles: [userTypeEnum.OperationManager],
    blockedPermissions: [userPermissionsEnum.View]
};

// Inline element for the connector route that has conditional QR logic
const ConnectorRouteElement = () => {
    const isQRCodeLink = !sessionStorage.getItem('oldFormatQRValidated') &&
        (sessionStorage.getItem('postLoginRedirect')?.includes('ChargePointStation') ||
            sessionStorage.getItem('postLoginRedirect')?.includes('connector'));
    if (isQRCodeLink) {
        return <OldFormatQRCodeRouteHandler />;
    }
    sessionStorage.removeItem('oldFormatQRValidated');
    return <ConnectorScreen />;
};

export default function Routes(props) {
    return (
        <Suspense fallback={<LoadingFallback />}>
            <RouterRoutes>
                {/* ============================================ */}
                {/* UNGUARDED ROUTES - Always accessible         */}
                {/* ============================================ */}
                <Route path='/payments' element={<PaymentsScreen />} />
                <Route path='/bills' element={<BillsScreen />} />
                <Route path='/accessibility-statement' element={<AccessibilityStatementScreen />} />
                <Route path='/privacyPolicy' element={<PrivacyPolicyScreen />} />

                {/* ============================================ */}
                {/* GUARDED ROUTES - Permission checked          */}
                {/* ============================================ */}

                {/* AUTH */}
                <Route path='/signup' element={<GuardedRoute {...DEFAULT_GUARD}><SignUpScreen cookies={props.cookies} /></GuardedRoute>} />
                <Route path='/forgotPassword' element={<GuardedRoute {...DEFAULT_GUARD}><ForgotPasswordScreen /></GuardedRoute>} />
                <Route path='/resetPassword' element={<GuardedRoute {...DEFAULT_GUARD}><ResetPasswordScreen /></GuardedRoute>} />

                {/* STRATEGY */}
                <Route path='/strategy/edit/:strategyId' element={<GuardedRoute {...DEFAULT_GUARD}><AddOrUpdateStrategyScreen /></GuardedRoute>} />
                <Route path='/strategy/add' element={<GuardedRoute {...DEFAULT_GUARD}><AddOrUpdateStrategyScreen /></GuardedRoute>} />
                <Route path='/strategy' element={<GuardedRoute {...DEFAULT_GUARD}><ChargingStrategiesScreen /></GuardedRoute>} />

                {/* LOCATIONS */}
                <Route path='/locations/add' element={<GuardedRoute {...DEFAULT_GUARD}><AddLocationScreen /></GuardedRoute>} />
                <Route path='/locations' element={<GuardedRoute {...DEFAULT_GUARD}><LocationsScreen /></GuardedRoute>} />

                {/* CHARGE POINTS - Most specific first */}
                <Route path='/ChargePointStation/Connectors/:selectedChargerId' element={<GuardedRoute {...DEFAULT_GUARD}><ConnectorListScreen /></GuardedRoute>} />
                <Route path='/ChargePointStation/:selectedChargerId/connector/:connectorId' element={<GuardedRoute {...DEFAULT_GUARD}><ConnectorRouteElement /></GuardedRoute>} />
                <Route path='/ChargePointStation/:selectedChargerId' element={<GuardedRoute {...DEFAULT_GUARD}><EditChargerScreen /></GuardedRoute>} />
                <Route path='/ChargePointStation' element={<GuardedRoute {...DEFAULT_GUARD}><ChargePointsScreen /></GuardedRoute>} />
                <Route path='/favChargers' element={<GuardedRoute {...DEFAULT_GUARD}><ChargePointsScreen showFavorite={true} /></GuardedRoute>} />

                {/* GROUPS */}
                <Route path='/groups/:groupId/editProfile/:profileId' element={<GuardedRoute {...DEFAULT_GUARD}><EditProfileScreen /></GuardedRoute>} />
                <Route path='/groups/:groupId/addProfile' element={<GuardedRoute {...DEFAULT_GUARD}><AddProfileScreen /></GuardedRoute>} />
                <Route path='/groups/:groupId/profiles' element={<GuardedRoute {...DEFAULT_GUARD}><MyProfilesScreen /></GuardedRoute>} />
                <Route path='/editGroup/:id' element={<GuardedRoute {...DEFAULT_GUARD}><AddOrEditGroupScreen /></GuardedRoute>} />
                <Route path='/addGroup' element={<GuardedRoute {...DEFAULT_GUARD}><AddOrEditGroupScreen /></GuardedRoute>} />
                <Route path='/myGroups' element={<GuardedRoute {...DEFAULT_GUARD}><MyGroups /></GuardedRoute>} />

                {/* BUSINESS GROUP */}
                <Route path='/business-group/edit/:businessGroupId' element={<GuardedRoute {...DEFAULT_GUARD}><AddOrEditBusinessGroupScreen /></GuardedRoute>} />
                <Route path='/business-group/add' element={<GuardedRoute {...DEFAULT_GUARD}><AddOrEditBusinessGroupScreen /></GuardedRoute>} />
                <Route path='/business-group' element={<GuardedRoute {...DEFAULT_GUARD}><MyBusinessGroups /></GuardedRoute>} />

                {/* DRIVER */}
                <Route path='/Driver/:driverId/Checklist' element={<GuardedRoute {...DEFAULT_GUARD}><PermissionsChecklist /></GuardedRoute>} />
                <Route path='/Driver/AddDriver' element={<GuardedRoute {...DEFAULT_GUARD}><AddDriverScreen /></GuardedRoute>} />
                <Route path='/Driver/:driverId' element={<GuardedRoute {...DEFAULT_GUARD}><EditDriverScreen /></GuardedRoute>} />
                <Route path='/Driver' element={<GuardedRoute {...DEFAULT_GUARD}><DriversScreen /></GuardedRoute>} />

                {/* RFID CARDS */}
                <Route path='/RFIDCards/AddTag' element={<GuardedRoute {...DEFAULT_GUARD}><AddRFIDTagScreen /></GuardedRoute>} />
                <Route path='/RFIDCards' element={<GuardedRoute {...DEFAULT_GUARD}><RFIDTagsScreen /></GuardedRoute>} />

                {/* CARS */}
                <Route path='/addCar' element={<GuardedRoute {...DEFAULT_GUARD}><AddCarScreen /></GuardedRoute>} />
                <Route path='/myCars' element={<GuardedRoute {...DEFAULT_GUARD}><MyCars /></GuardedRoute>} />

                {/* CHARGER */}
                <Route path='/addCharger' element={<GuardedRoute {...DEFAULT_GUARD}><AddChargerScreen /></GuardedRoute>} />

                {/* OPERATOR */}
                <Route path='/OperatorChargePoints' element={<GuardedRoute {...DEFAULT_GUARD}><OperatorChargePoints /></GuardedRoute>} />

                {/* BILLING */}
                <Route path='/updateBilling' element={<GuardedRoute {...DEFAULT_GUARD}><UpdateBillingScreen /></GuardedRoute>} />

                {/* SETTINGS & USER */}
                <Route path='/settings' element={<GuardedRoute {...DEFAULT_GUARD}><SettingsScreen cookies={props.cookies} /></GuardedRoute>} />
                <Route path='/personal' element={<GuardedRoute {...DEFAULT_GUARD}><PersonalScreen cookies={props.cookies} /></GuardedRoute>} />

                {/* QR Scanner */}
                <Route path='/QR' element={<GuardedRoute {...DEFAULT_GUARD}><QrScannarScreen /></GuardedRoute>} />

                {/* ROOT */}
                <Route path='/' element={<GuardedRoute {...DEFAULT_GUARD}><LoginScreen cookies={props.cookies} /></GuardedRoute>} />

                {/* ============================================ */}
                {/* QR CATCH-ALL - MUST BE LAST                  */}
                {/* All-dynamic segments rank lowest in v6       */}
                {/* ============================================ */}
                <Route path='/:partyID/:uid/:connector_id' element={<GuardedRoute {...DEFAULT_GUARD}><QRCodeRouteHandler /></GuardedRoute>} />

                {/* Unknown routes while authenticated → fall back to charge points list */}
                <Route path='*' element={<Navigate to='/ChargePointStation' replace />} />
            </RouterRoutes>
        </Suspense>
    );
}

/**
 * LoginRoutes - Routes for unauthenticated users (React Router v6)
 */
export function LoginRoutes(props) {
    return (
        <Suspense fallback={<LoadingFallback />}>
            <RouterRoutes>
                <Route path='/forgotPassword' element={<ForgotPasswordScreen />} />
                <Route path='/resetPassword' element={<ResetPasswordScreen />} />
                <Route path='/signup' element={<SignUpScreen cookies={props.cookies} />} />
                <Route path='/ChargePointStation/:selectedChargerId/connector/:connectorId' element={<OldFormatQRCodeRouteHandler />} />
                <Route path='/:partyID/:uid/:connector_id' element={<QRCodeRouteHandler />} />
                <Route path='/' element={<LoginScreen cookies={props.cookies} />} />
                <Route path='*' element={<Navigate to='/' replace />} />
            </RouterRoutes>
        </Suspense>
    );
}
